/* © Facilogi. See COPYRIGHT file for full copyright & licensing details. */

import Feathers from "@feathersjs/feathers";

import { Autobind } from "@IBS/Frontend/Utils";
import { Models } from "@Internal";
import { AnnotatedJSON, HandleListError } from "App/Contracts";
import {
    MigrateBatchResult, MigrateFailure, MigrationCandidate, MigrationHistoryPage,
    RetryMigrationResult
} from "App/Contracts/Subscriptions";
import { Recurrence } from "Shared/Models";
import {
    CustomError, GroupedLinesTargetByProduct, Subscription, SubscriptionLine, SubscriptionMigration
} from "App/Models";

import BaseService, { ProcessException } from "./Base";

export default class Subscriptions extends BaseService<Subscription> {
    private unsubscribeService: Feathers.Service<any>;
    private groupTargetLinesService: Feathers.Service<any>;
    private changeRecurrenceService: Feathers.Service<any>;
    private migrateAllService: Feathers.Service<any>;
    private outdatedPlanIdsService: Feathers.Service<any>;
    private migrationHistoryService: Feathers.Service<any>;
    private retryMigrationService: Feathers.Service<any>;
    private migrationCandidatesService: Feathers.Service<any>;

    constructor(
        service: Feathers.Service<any>,
        groupTargetLinesService: Feathers.Service<any>,
        unsubscribeService: Feathers.Service<any>,
        changeRecurrenceService: Feathers.Service<any>,
        migrateAllService: Feathers.Service<any>,
        outdatedPlanIdsService: Feathers.Service<any>,
        migrationHistoryService: Feathers.Service<any>,
        retryMigrationService: Feathers.Service<any>,
        migrationCandidatesService: Feathers.Service<any>
    ) {
        super( service );

        this.unsubscribeService = unsubscribeService;
        this.groupTargetLinesService = groupTargetLinesService;
        this.changeRecurrenceService = changeRecurrenceService;
        this.migrateAllService = migrateAllService;
        this.outdatedPlanIdsService = outdatedPlanIdsService;
        this.migrationHistoryService = migrationHistoryService;
        this.retryMigrationService = retryMigrationService;
        this.migrationCandidatesService = migrationCandidatesService;
    }

    @Autobind
    async GroupPriceTargetedLinesByProduct(
        record: Subscription
    ): HandleListError<GroupedLinesTargetByProduct> {
        let results: any;
        const groupedLines: GroupedLinesTargetByProduct[] = [];

        try {
            results = await this.groupTargetLinesService.get( record.Id );

            if( results.GroupedLines === undefined )
                return [ [], undefined ];

            for( const values of Object.values( results.GroupedLines ) ) {
                const priceTargetLines: SubscriptionLine[] = [];

                const [ lines, linesError ] =
                    Models.Deserialize<AnnotatedJSON>( values as AnnotatedJSON );

                if( linesError !== undefined || lines === undefined )
                    return [ [], linesError ];

                for( const value of Object.values( lines ) )
                    priceTargetLines.push( value );

                const [ line ] = priceTargetLines;

                const title = line.Plan === undefined
                    ? line.Product.Name
                    : line.Plan.Name;

                groupedLines.push({
                    Title: title || "",
                    LinesTarget: priceTargetLines
                });
            }

        } catch( exception ) {
            const error = await ProcessException( exception );

            return [ [], error ];
        }

        return [ groupedLines, undefined ];
    }

    @Autobind
    async Unsubscribe(
        record: Subscription,
        immediate: boolean = false
    ): Promise<CustomError | undefined> {
        try {
            const results = await this.unsubscribeService.update( record.Id, { immediate } );

            if( results.Error !== undefined )
                return new CustomError( results.Error.Id, results.Error.Message );

        } catch( error ) {
            return ProcessException( error );
        }
        return;
    }

    /* Apply the current published price versions to existing Active subscriptions still on an older
       snapshot, re-billing each. Processes one bounded batch of subscriptions (cursor-driven) and
       returns its result; the caller loops, passing NextCursor back, until HasMore is false. This
       keeps each request short so re-billing hundreds of customers can't time out as a 502. The id
       passed to the endpoint is a placeholder — the action is global, not bound to one record. */
    @Autobind
    async MigrateAll(
        locale: string,
        planIds: number[] = [],
        cursorId: number = 0,
        runId: string = "",
        subscriptionIds: number[] = []
    ): Promise<[ MigrateBatchResult | undefined, CustomError | undefined ]> {
        try {
            const results = await this.migrateAllService.update(
                0,
                {
                    Locale: locale,
                    PlanIds: planIds,
                    CursorId: cursorId,
                    RunId: runId,
                    SubscriptionIds: subscriptionIds
                }
            );

            if( results !== undefined && results.Error !== undefined )
                return [ undefined, new CustomError( results.Error.Id, results.Error.Message ) ];

            const failures: MigrateFailure[] = ( results !== undefined
                && Array.isArray( results.Failures ) )
                ? results.Failures
                : [];

            const batch: MigrateBatchResult = {
                Migrated: ( results !== undefined && results.Migrated !== undefined )
                    ? Number( results.Migrated )
                    : 0,
                Failures: failures,
                NextCursor: ( results !== undefined && results.NextCursor !== undefined )
                    ? Number( results.NextCursor )
                    : 0,
                HasMore: results !== undefined && results.HasMore === true,
                Total: ( results !== undefined && results.Total !== undefined )
                    ? Number( results.Total )
                    : 0,
                Examined: ( results !== undefined && results.Examined !== undefined )
                    ? Number( results.Examined )
                    : 0
            };

            return [ batch, undefined ];

        } catch( error ) {
            return [ undefined, await ProcessException( error ) ];
        }
    }

    /* Les comptes que la migration toucherait sur ces plans, avec leur montant avant / apres.
       Lecture seule cote back : rien n'est migre ni facture. */
    @Autobind
    async MigrationCandidates(
        planIds: number[]
    ): Promise<[ MigrationCandidate[], CustomError | undefined ]> {
        try {
            const results = await this.migrationCandidatesService.update( 0, { PlanIds: planIds } );

            if( results !== undefined && results.Error !== undefined )
                return [ [], new CustomError( results.Error.Id, results.Error.Message ) ];

            const candidates: MigrationCandidate[] = (
                results !== undefined && Array.isArray( results.Candidates )
            )
                ? results.Candidates.map( ( raw: any ): MigrationCandidate => ( {
                    Id: Number( raw.Id ),
                    CustomerId: raw.CustomerId !== undefined ? Number( raw.CustomerId ) : undefined,
                    CustomerName: String( raw.CustomerName || "" ),
                    PlanName: String( raw.PlanName || "" ),
                    Recurrence: raw.Recurrence,
                    NextInvoiceDate: raw.NextInvoiceDate,
                    Before: Number( raw.Before || 0 ),
                    After: Number( raw.After || 0 )
                } ) )
                : [];

            return [ candidates, undefined ];

        } catch( error ) {
            return [ [], await ProcessException( error ) ];
        }
    }

    /* Plans that still have Active subscriptions on an outdated price snapshot, i.e. plans where
       "apply the new prices" would re-bill someone. The console uses this to flag those plan
       columns; the list shrinks as the bulk migration runs. */
    @Autobind
    async OutdatedPlanIds(): Promise<[ number[], CustomError | undefined ]> {
        try {
            const results = await this.outdatedPlanIdsService.get( 0 );

            if( results !== undefined && results.Error !== undefined )
                return [ [], new CustomError( results.Error.Id, results.Error.Message ) ];

            const planIds = ( results !== undefined && Array.isArray( results.PlanIds ) )
                ? results.PlanIds.map( ( value: unknown ): number => Number( value ) )
                : [];

            return [ planIds, undefined ];

        } catch( error ) {
            return [ [], await ProcessException( error ) ];
        }
    }

    /* One page of the bulk "Apply prices to customers" history, most recent first. One row per
       subscription outcome (Migrated / Failed) of each run. Rows are plain backend JSON. */
    @Autobind
    async MigrationHistory(
        page: number = 1,
        limit: number = 20,
        status?: string
    ): Promise<[ MigrationHistoryPage | undefined, CustomError | undefined ]> {
        try {
            const query: Record<string, number | string> = { Page: page, Limit: limit };

            if( status !== undefined )
                query.Status = status;

            const results = await this.migrationHistoryService.find( { query } );

            /* The global serialization after-hook reshapes `{ Records, PagerSorter }` into
               `{ Items, Pagination }`, so read `Items` (keep `Records` as a defensive fallback). */
            const items = ( results !== undefined && results.Items !== undefined )
                ? results.Items
                : ( results !== undefined && results.Records !== undefined )
                    ? results.Records
                    : [];

            const pagination = ( results !== undefined && results.Pagination !== undefined )
                ? results.Pagination
                : {};

            const pageResult: MigrationHistoryPage = {
                Items: items as SubscriptionMigration[],
                Page: pagination.Page !== undefined ? Number( pagination.Page ) : page,
                Pages: pagination.Pages !== undefined ? Number( pagination.Pages ) : 1,
                Total: pagination.Total !== undefined ? Number( pagination.Total ) : items.length,
                Limit: pagination.Limit !== undefined ? Number( pagination.Limit ) : limit
            };

            return [ pageResult, undefined ];

        } catch( error ) {
            return [ undefined, await ProcessException( error ) ];
        }
    }

    /* Replay the migration of a single subscription from a Failed history row. Generates a RunId so
       the retry outcome (Migrated / Failed) is logged as a fresh history row. Returns whether it
       migrated this time plus the failure message when it fails again. */
    @Autobind
    async RetryMigration(
        subscriptionId: number,
        locale: string
    ): Promise<[ RetryMigrationResult | undefined, CustomError | undefined ]> {
        try {
            const runId = `retry-${ Date.now() }`;

            const results = await this.retryMigrationService.update(
                subscriptionId,
                { Locale: locale, RunId: runId }
            );

            if( results !== undefined && results.Error !== undefined )
                return [ undefined, new CustomError( results.Error.Id, results.Error.Message ) ];

            const result: RetryMigrationResult = {
                Migrated: results !== undefined && results.Migrated === true,
                Message: ( results !== undefined && results.Message !== undefined )
                    ? String( results.Message )
                    : undefined
            };

            return [ result, undefined ];

        } catch( error ) {
            return [ undefined, await ProcessException( error ) ];
        }
    }

    @Autobind
    async ChangeRecurrence(
        record: Subscription,
        recurrence: Recurrence
    ): Promise<CustomError | undefined> {
        try {
            const results =
                await this.changeRecurrenceService.update( record.Id, { Recurrence: recurrence } );

            if( results !== undefined && results.Error !== undefined )
                return new CustomError( results.Error.Id, results.Error.Message );

        } catch( error ) {
            return ProcessException( error );
        }
        return;
    }
}
